import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
    Box,
    Badge,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Link,
    List,
    ListItem,
    ListItemText,
    Typography,
    Switch,
    FormControlLabel,
    TextField,
    Select,
    MenuItem
} from "@mui/material/";
import CloseIcon from "@mui/icons-material/Close";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DriveFileRenameOutlineSharpIcon from "@mui/icons-material/DriveFileRenameOutlineSharp";
import { styled } from "@mui/material/styles";
import ImageGallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { DateTimePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import { strings } from "../../../../../common/i18n";
import styles from "../styles";
import { helpers, format } from "../../../../../common/lib";
import Loading from "../../../../../common/components/Loading";
import actions from "../../../../../Store/actions";
import AddDealerDetail from "./AddDealerDetail";
import { logToSentry } from "../../../../../config/Sentry";

const CARD_HEIGHT = 250;

const renderDateTimePicker = (label, value, onChange, error) => (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DateTimePicker
            label={label}
            value={value}
            onChange={onChange}
            disableFuture
            sx={{ mb: 1 }}
            ampm={false}
            slotProps={{
                textField: {
                    size: "small",
                    fullWidth: true
                    // error: !!error,
                    // helperText: error || " "
                }
            }}
        />
    </LocalizationProvider>
);

export default function SignatureGallery({ dealers = [] }) {
    const dispatch = useDispatch();
    const {
        accounts,
        assignmentDetails,
        dealerContacts = [],
        typesList = {}
    } = useSelector((state) => state);
    const { auditId } = useParams();

    const [signeesModal, setSigneesModal] = useState(false);
    const [mode, setMode] = useState("");
    const [loading, setLoading] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [editedSignees, setEditedSignees] = useState({});
    const [loadingText, setLoadingText] = useState("");

    const getRoleDetails = (roleId) =>
        helpers.getListItemForId(typesList.roles, roleId)?.name;
    const getAuditor = (auditorId) =>
        helpers.getListItemForId(accounts, auditorId)?.displayName;

    const handleDeleteDealer = async (dealer) => {
        if (dealerContacts.length <= 1) return;
        if (!window.confirm(strings("messages.signatureDelete"))) return;

        try {
            setLoadingText(strings("deletingSignature"));
            setLoading(true);
            await dispatch(
                actions.dealerContacts.create.deleteRequest({ id: dealer })
            );
            setCurrentIndex(0); //
            // Refresh store
            await Promise.all([
                dispatch(actions.auditReview.create.getRequest(auditId)),
                dispatch(actions.assignmentDetails.create.getRequest(auditId))
            ]);
            dispatch(
                actions.toast.create.success(
                    strings("messages.signatureDeleted")
                )
            );
        } catch (error) {
            logToSentry("On deleting signature:", { error });
            dispatch(
                actions.toast.create.error(strings("messages.signatureDelete"))
            );
        } finally {
            setLoading(false);
            setLoadingText("");
        }
    };

    const getExtension = (path) => {
        if (!path) return "img";

        // Case 1: Base64 / data URL
        if (path.startsWith("data:")) {
            const match = path.match(/^data:(image|application)\/([^;]+);/i);
            return match ? match[2].toLowerCase() : "img";
        }

        // Case 2: Remote URL
        try {
            const cleanUrl = path.split("?")[0]; // remove query params
            const lastDot = cleanUrl.lastIndexOf(".");

            // No extension found
            if (lastDot === -1) return "img";

            const ext = cleanUrl.substring(lastDot + 1).toLowerCase();

            // Defensive validation: allow only typical safe characters
            if (/^[a-z0-9+]+$/i.test(ext)) {
                return ext;
            }

            return "img";
        } catch (error) {
            logToSentry("Error getting file extension:", { error });
            return "img";
        }
    };

    const handleImageDownload = async (data, filename = "signature", type) => {
        const path = type === "dealer" ? data : data;

        if (!path) return;

        const isData = path.startsWith("data:");
        const ext = getExtension(path);

        if (isData) {
            // Direct download
            const link = document.createElement("a");
            link.href = path;
            link.download = `${filename}.${ext}`;
            link.click();
            return;
        }

        try {
            // Fetch remote file as blob
            const response = await fetch(path, { mode: "cors" });
            if (!response.ok) throw new Error("Failed to fetch file");

            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = `${filename}.${ext}`;
            link.click();

            URL.revokeObjectURL(blobUrl); // clean up
        } catch (error) {
            console.error("Download failed:", error);
            logToSentry("Download failed:", { error });
            alert("Cannot download file. Maybe CORS issue.");
        }
    };

    const handleUpload = (field) => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "image/*";
        input.onchange = (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            const reader = new FileReader();
            reader.onload = (event) => {
                setEditedSignees((prev) => ({
                    ...prev,
                    [field]: event.target.result
                }));
            };
            reader.readAsDataURL(file);
        };
        input.click();
    };

    const populateEditedFromDealer = useCallback(
        (dealerItem) => {
            // Identify corresponding auditor
            const auditor =
                assignmentDetails.data.find(
                    (item) => item.id === dealerItem.assignment
                ) || {};

            if (!dealerItem) return;
            setEditedSignees({
                dealerContactEmail: dealerItem.email || "",
                dealerContactName: dealerItem.name || "",
                dealerContactRole: dealerItem.roleId || "",
                dealerContactComment: dealerItem.comment || "",
                dealerContactSignature: dealerItem.signature?.path || "",
                dealerContactSignatureDate:
                    dealerItem.signatureDate ||
                    new Date(Date.now()).toISOString(),
                auditorComments: auditor?.auditorComment || "",
                auditorSignature: auditor?.auditorSignature?.path || "",
                auditorSignatureDate:
                    auditor?.auditorSignatureDate ||
                    new Date(Date.now()).toISOString(),
                audit: parseInt(auditId),
                auditAssignment: auditor?.id
            });
        },
        [auditId, assignmentDetails]
    );

    const auditorSignatureChanged = useMemo(() => {
        return (
            mode === "add" ||
            (!!editedSignees.auditorSignature &&
                editedSignees.auditorSignature.startsWith("data:image"))
        );
    }, [editedSignees.auditorSignature, mode]);

    useEffect(() => {
        if (!editMode) {
            const dealer = dealerContacts[currentIndex] || {};
            if (dealer && Object.keys(dealer).length > 0) {
                populateEditedFromDealer(dealer);
            }
        }
    }, [editMode, currentIndex, dealerContacts, populateEditedFromDealer]);

    const handleSave = async (data) => {
        setLoadingText(strings("updatingSignatures"));

        const assignment = assignmentDetails.data[0] || {};
        const isAddMode = mode === "add";

        // Helper: decide how to send signature
        const getSignatureValue = (signature) => {
            if (!signature) return null;
            if (signature.startsWith("data:image")) return signature;
            if (signature.startsWith("https://")) return null;
            return null;
        };

        const dealerPayload = {
            dealerContactEmail: isAddMode
                ? data.dealerContactEmail
                : editedSignees.dealerContactEmail,
            dealerContactName: isAddMode
                ? data.dealerContactName
                : editedSignees.dealerContactName,
            dealerContactRole: isAddMode
                ? data.dealerContactRole
                : editedSignees.dealerContactRole,
            dealerContactComment: isAddMode
                ? data.dealerContactComment
                : editedSignees.dealerContactComment,
            dealerContactSignature: isAddMode
                ? getSignatureValue(data.dealerContactSignature)
                : getSignatureValue(editedSignees.dealerContactSignature),
            dealerContactSignatureDate: isAddMode
                ? new Date(data.dealerContactSignatureDate).toISOString()
                : new Date(
                      editedSignees.dealerContactSignatureDate
                  ).toISOString(),
            // ||new Date().toISOString(),
            audit: parseInt(auditId),
            auditAssignment:
                data?.auditAssignment || editedSignees?.auditAssignment
        };

        // --- Fix: compare auditor fields safely ---
        const currentAuditorComment =
            typeof assignment.auditorComment === "string"
                ? assignment.auditorComment.trim()
                : "";

        const newAuditorComment =
            typeof editedSignees.auditorComments === "string"
                ? editedSignees.auditorComments.trim()
                : "";

        const auditorCommentChanged =
            newAuditorComment !== currentAuditorComment;

        // Only post auditor if explicitly changed or in add mode
        const shouldPostAuditor =
            isAddMode || auditorSignatureChanged || auditorCommentChanged;

        const auditorPayload = {
            auditorComment: isAddMode
                ? data.auditorComments
                : editedSignees.auditorComments,
            auditorSignatureDate: isAddMode
                ? new Date(data.auditorSignatureDate).toISOString()
                : new Date(editedSignees.auditorSignatureDate).toISOString(),
            // || new Date().toISOString(),
            auditorSignature: isAddMode
                ? getSignatureValue(data.auditorSignature)
                : getSignatureValue(editedSignees.auditorSignature),
            auditAssignment:
                (data && data?.auditAssignment) ||
                editedSignees?.auditAssignment
        };

        try {
            setLoading(true);

            await dispatch(
                actions.dealerContacts.create.postRequest(dealerPayload)
            );

            await dispatch(
                actions.dealerContacts.create.postRequest(auditorPayload)
            );

            // Refresh store
            await Promise.all([
                dispatch(actions.auditReview.create.getRequest(auditId)),
                dispatch(actions.assignmentDetails.create.getRequest(auditId))
            ]);

            dispatch(
                actions.toast.create.success(
                    strings("messages.signatureUpdated")
                )
            );

            setEditMode(false);
            setMode("view");
        } catch (error) {
            console.error("Error saving signatures:", error);
            logToSentry("On saving signatures:", { error });
            dispatch(
                actions.toast.create.error(
                    strings("errors.signatureSaveFailed")
                )
            );
        } finally {
            setLoading(false);
            setLoadingText("");
        }
    };

    const renderDetailRow = (label, value) => (
        <Typography variant="body2" sx={{ mb: 0.8, textAlign: "left" }}>
            <strong>{strings(label)}:</strong> {value || "-"}
        </Typography>
    );

    const renderCombinedSlide = (dealerItem, auditorItem) => (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 2,
                p: 3,
                mb: 4,
                backgroundColor: "#fff",
                position: "relative",
                boxShadow: 1,
                minHeight: CARD_HEIGHT
            }}
        >
            {/* ===== Dealer Section ===== */}
            <Typography
                variant="h6"
                sx={{
                    mb: 2,
                    textAlign: "center",
                    fontWeight: "bold"
                }}
            >
                {strings("dealer")}
            </Typography>

            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    width: "100%",
                    gap: 4,
                    position: "relative"
                }}
            >
                {dealerContacts.length > 1 && (
                    <IconButton
                        color="error"
                        sx={{ position: "absolute", top: 0, right: 0 }}
                        onClick={() => handleDeleteDealer(dealerItem.id)}
                    >
                        <DeleteOutlineIcon />
                    </IconButton>
                )}

                {/* Signature + Buttons */}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        flex: 1,
                        position: "relative"
                    }}
                >
                    <img
                        src={
                            mode === "add"
                                ? dealerItem.signature?.path
                                : editedSignees.dealerContactSignature
                        }
                        alt={dealerItem.name}
                        style={{
                            maxHeight: 150,
                            objectFit: "contain"
                        }}
                    />

                    <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                        {!editMode && (
                            <Button
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={() =>
                                    handleImageDownload(
                                        dealerItem.signature?.path,
                                        `${
                                            dealerItem.name || "dealer"
                                        }-signature`,
                                        "dealer"
                                    )
                                }
                            >
                                {strings("download")}
                            </Button>
                        )}
                        {editMode && (
                            <Button
                                variant="outlined"
                                startIcon={<FileUploadIcon />}
                                onClick={() =>
                                    handleUpload("dealerContactSignature")
                                }
                            >
                                {strings("upload")}
                            </Button>
                        )}
                    </Box>
                </Box>

                {/* Dealer Details */}
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center"
                    }}
                >
                    {editMode ? (
                        <>
                            <TextField
                                label="Dealer Name"
                                size="small"
                                fullWidth
                                sx={{ mb: 1 }}
                                value={editedSignees.dealerContactName || ""}
                                onChange={(e) =>
                                    setEditedSignees((prev) => ({
                                        ...prev,
                                        dealerContactName: e.target.value
                                    }))
                                }
                            />
                            {/* <Grid item xs={6}> */}
                            {renderDateTimePicker(
                                "Dealer Signature Date",
                                editedSignees.dealerContactSignatureDate
                                    ? dayjs(
                                          editedSignees.dealerContactSignatureDate
                                      )
                                    : null,
                                (newValue) =>
                                    setEditedSignees((prev) => ({
                                        ...prev,
                                        dealerContactSignatureDate: newValue
                                    }))
                            )}
                            {/* </Grid> */}
                            <TextField
                                label="Email"
                                size="small"
                                fullWidth
                                sx={{ mb: 1 }}
                                value={editedSignees.dealerContactEmail || ""}
                                onChange={(e) =>
                                    setEditedSignees((prev) => ({
                                        ...prev,
                                        dealerContactEmail: e.target.value
                                    }))
                                }
                            />
                            <Select
                                size="small"
                                fullWidth
                                sx={{ mb: 1 }}
                                value={editedSignees.dealerContactRole || ""}
                                onChange={(e) =>
                                    setEditedSignees((prev) => ({
                                        ...prev,
                                        dealerContactRole: e.target.value
                                    }))
                                }
                                displayEmpty
                            >
                                <MenuItem key="select-role-empty" value="">
                                    <em>{strings("selectRole")}</em>
                                </MenuItem>
                                {(typesList.roles?.data || []).map((role) => (
                                    <MenuItem key={role.id} value={role.id}>
                                        {role.name}
                                    </MenuItem>
                                ))}
                            </Select>
                            <TextField
                                label="Dealer Comments"
                                size="small"
                                fullWidth
                                multiline
                                rows={2}
                                value={editedSignees.dealerContactComment || ""}
                                onChange={(e) =>
                                    setEditedSignees((prev) => ({
                                        ...prev,
                                        dealerContactComment: e.target.value
                                    }))
                                }
                            />
                        </>
                    ) : (
                        <>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontWeight: "bold",
                                    mb: 1,
                                    fontSize: "1rem"
                                }}
                            >
                                {dealerItem.name}
                            </Typography>
                            {renderDetailRow(
                                "signatureDate",
                                format.displayDatesWithTime(
                                    dealerItem.signatureDate
                                )
                            )}
                            {renderDetailRow("email", dealerItem.email)}
                            {renderDetailRow(
                                "role",
                                getRoleDetails(dealerItem.roleId)
                            )}
                            {renderDetailRow(
                                "dealerComments",
                                dealerItem.comment
                            )}
                        </>
                    )}
                </Box>
            </Box>

            {/* Divider */}
            <Box
                sx={{
                    width: "90%",
                    height: "1px",
                    backgroundColor: "#e0e0e0",
                    my: 3
                }}
            />

            {/* ===== Auditor Section ===== */}
            <Typography
                variant="h6"
                sx={{
                    mb: 2,
                    textAlign: "center",
                    fontWeight: "bold"
                }}
            >
                {strings("auditor")}
            </Typography>

            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    width: "100%",
                    gap: 4
                }}
            >
                {/* Auditor Signature */}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        flex: 1
                    }}
                >
                    <img
                        src={
                            mode === "add"
                                ? auditorItem?.auditorSignature.path
                                : editedSignees.auditorSignature
                        }
                        alt={getAuditor(auditorItem?.auditor)}
                        style={{
                            maxHeight: 150,
                            objectFit: "contain"
                        }}
                    />
                    <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                        {!editMode && (
                            <Button
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={() =>
                                    handleImageDownload(
                                        auditorItem?.auditorSignature?.path,
                                        `${
                                            getAuditor(auditorItem?.auditor) ||
                                            "auditor"
                                        }-signature`,
                                        "auditor"
                                    )
                                }
                            >
                                {strings("download")}
                            </Button>
                        )}
                        {editMode && (
                            <Button
                                variant="outlined"
                                startIcon={<FileUploadIcon />}
                                onClick={() => handleUpload("auditorSignature")}
                            >
                                {strings("upload")}
                            </Button>
                        )}
                    </Box>
                </Box>

                {/* Auditor Details */}
                <Box sx={{ flex: 1 }}>
                    {editMode ? (
                        <>
                            {renderDateTimePicker(
                                "Auditor Signature Date",
                                editedSignees.auditorSignatureDate
                                    ? dayjs(editedSignees.auditorSignatureDate)
                                    : null,
                                (newValue) =>
                                    setEditedSignees((prev) => ({
                                        ...prev,
                                        auditorSignatureDate: newValue
                                    }))
                            )}
                            <TextField
                                label="Auditor Comments"
                                size="small"
                                fullWidth
                                multiline
                                rows={2}
                                value={editedSignees.auditorComments || ""}
                                disabled={!auditorSignatureChanged}
                                helperText={
                                    !editedSignees.auditorSignature ||
                                    !editedSignees.auditorSignature.startsWith(
                                        "data:image"
                                    )
                                        ? strings(
                                              "messages.disabledAuditorSignature"
                                          )
                                        : ""
                                }
                                onChange={(e) =>
                                    setEditedSignees((prev) => ({
                                        ...prev,
                                        auditorComments: e.target.value
                                    }))
                                }
                            />
                        </>
                    ) : (
                        <>
                            <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: "bold", mb: 1 }}
                            >
                                {getAuditor(auditorItem?.auditor)}
                            </Typography>
                            {renderDetailRow(
                                "signatureDate",
                                format.displayDatesWithTime(
                                    auditorItem?.auditorSignatureDate
                                )
                            )}

                            {renderDetailRow(
                                "auditorComments",
                                auditorItem?.auditorComment
                            )}
                        </>
                    )}
                </Box>
            </Box>

            {editMode && (
                <Box sx={{ display: "flex", gap: 1, mt: 3 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleSave()}
                    >
                        {strings("save")}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => setEditMode(false)}
                    >
                        {strings("cancel")}
                    </Button>
                </Box>
            )}
        </Box>
    );

    const combinedGalleryItems = dealerContacts.map((dealer, i) => {
        const auditor =
            assignmentDetails.data.find(
                (item) => item.id === dealer.assignment
            ) || {};

        return {
            key: dealer.id || i,
            renderItem: () => renderCombinedSlide(dealer, auditor)
        };
    });

    const StyledBadge = styled(Badge)(({ theme }) => ({
        "& .MuiBadge-badge": {
            right: -15,
            top: 13,
            padding: "0 4px"
        }
    }));

    return (
        <>
            <Box sx={styles.mainHeaderContainer}>
                <StyledBadge
                    badgeContent={dealerContacts.length}
                    color="secondary"
                >
                    <DriveFileRenameOutlineSharpIcon color="primary" />
                    <Typography variant="body1" sx={{ ml: 1 }}>
                        {strings("signatures")}
                    </Typography>
                </StyledBadge>

                <Link
                    component="button"
                    variant="caption"
                    onClick={() => {
                        if (dealerContacts.length === 0) {
                            setMode("add");
                        }

                        setSigneesModal(true);
                    }}
                >
                    {dealerContacts.length > 0
                        ? strings("showMore")
                        : strings("add")}
                </Link>
            </Box>

            <List sx={styles.auditorList}>
                {dealerContacts.map((item) => (
                    <ListItem key={item.id} sx={styles.listItem}>
                        <ListItemText
                            primary={
                                <Typography variant="caption">
                                    {item.name}
                                </Typography>
                            }
                        />
                    </ListItem>
                ))}
            </List>

            <Dialog
                fullWidth
                maxWidth="md"
                open={signeesModal}
                onClose={() => {
                    setSigneesModal(false);
                    setEditMode(false);
                }}
            >
                <DialogTitle sx={{ textAlign: "center" }}>
                    {strings("signeesDetails")}
                </DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={() => {
                        setSigneesModal(false);
                        setEditMode(false);
                    }}
                    sx={styles.signeeCloseButton}
                >
                    <CloseIcon color="primary" />
                </IconButton>

                <DialogContent dividers sx={{ minHeight: CARD_HEIGHT + 100 }}>
                    {loading ? (
                        <Box
                            sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                backgroundColor: "rgba(255,255,255,0.6)", // optional: subtle overlay
                                zIndex: 10
                            }}
                        >
                            <Typography variant="body2">
                                {loadingText}...
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {mode === "add" ? (
                                <>
                                    <AddDealerDetail
                                        mode="add"
                                        initialData={editedSignees}
                                        onSave={handleSave}
                                        onCancel={() => setSigneesModal(false)}
                                        roles={typesList.roles.data}
                                    />
                                </>
                            ) : (
                                <>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "flex-end",
                                            mb: 2
                                        }}
                                    >
                                        {dealerContacts.length === 1 && (
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={editMode}
                                                        onChange={() =>
                                                            setEditMode(
                                                                (prev) => !prev
                                                            )
                                                        }
                                                    />
                                                }
                                                label={strings("edit")}
                                            />
                                        )}
                                    </Box>

                                    {dealerContacts.length > 1 ? (
                                        <ImageGallery
                                            items={combinedGalleryItems}
                                            startIndex={currentIndex}
                                            onSlide={(index) =>
                                                setCurrentIndex(index)
                                            }
                                            showThumbnails={false}
                                            showPlayButton={false}
                                            showFullscreenButton={false}
                                            showNav
                                        />
                                    ) : dealerContacts.length === 1 ? (
                                        // Just show the single dealer directly, without ImageGallery
                                        renderCombinedSlide(
                                            dealerContacts[0],
                                            assignmentDetails.data.find(
                                                (item) =>
                                                    item.id ===
                                                    dealerContacts[0].assignment
                                            ) || {}
                                        )
                                    ) : (
                                        // Optional fallback — no dealers
                                        <Typography
                                            variant="body2"
                                            sx={{ textAlign: "center", mt: 4 }}
                                        >
                                            {strings("messages.noDealersFound")}
                                        </Typography>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}
